/**
 * service-service-desk client — operator-scoped surfaces (B + C merged).
 *
 * (1) Sub-project C slice 4 — case workflow (operator transitions complaints):
 *   GET    /v1/service-desk/cases               — listServiceCases
 *   GET    /v1/service-desk/cases/:id           — getServiceCase
 *   POST   /v1/service-desk/cases/:id/transition — transitionCase
 *   POST   /v1/service-desk/cases/:id/comments   — addCaseComment
 *   GET    /v1/service-desk/cases/:id/comments   — listCaseComments
 *   POST   /v1/service-desk/cases/:id/escalate   — escalateCase
 *   POST   /v1/service-desk/cases/:id/resolve    — resolveCase
 *
 * (2) Sub-project B slice 10 — admin surface (form catalog + exports + help):
 *   GET    /v1/service-desk/dashboard
 *   GET    /v1/service-desk/applications
 *   GET    /v1/service-desk/forms (POST/PUT/DELETE)
 *   POST/GET /v1/service-desk/exports (+ /:id/download)
 *   GET    /v1/service-desk/help-articles (POST/PUT/DELETE)
 *
 * Case wire shape from CaseService.toServiceDeskCaseResponse:
 *   id, caseNumber, tenantId, operatorId, formKey, agency, category,
 *   customerId, title, formData, subjectRefHash, status, priority,
 *   assigneeId, escalationTier, slaTarget, resolutionType, resolutionNote,
 *   paymentId, openedBy, openedAt, resolvedAt, closedAt, updatedAt.
 */
import { env } from '../env';

import { request } from './client';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type CaseStatus =
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'AWAITING_CUSTOMER'
  | 'ESCALATED'
  | 'RESOLVED'
  | 'CLOSED'
  | 'REJECTED';

export type CasePriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export type CaseTransitionEvent =
  | 'ASSIGN'
  | 'AWAIT_CUSTOMER'
  | 'REOPEN'
  | 'RESOLVE'
  | 'CLOSE'
  | 'ESCALATE'
  | 'REJECT';

/** Wire shape returned by the backend ServiceDeskCaseResponseDto. */
export interface ServiceDeskCase {
  readonly id: string;
  readonly caseNumber: string;
  readonly tenantId: string;
  readonly operatorId: number;
  readonly formKey: string;
  readonly agency: string;
  readonly category: string;
  readonly customerId: string | null;
  readonly title: string;
  readonly formData: Record<string, unknown>;
  readonly subjectRefHash: string | null;
  readonly status: CaseStatus;
  readonly priority: CasePriority;
  readonly assigneeId: string | null;
  readonly escalationTier: number;
  readonly slaTarget: string | null;
  readonly resolutionType: string | null;
  readonly resolutionNote: string | null;
  readonly paymentId: string | null;
  readonly openedBy: string;
  readonly openedAt: string;
  readonly resolvedAt: string | null;
  readonly closedAt: string | null;
  readonly updatedAt: string;
}

/** Wire shape returned for comments (Sub-project C slice 4). */
export interface ServiceDeskComment {
  readonly id: string;
  readonly caseId: string;
  readonly visibility: 'PUBLIC' | 'INTERNAL';
  readonly body: string;
  readonly authorId: string;
  readonly authorRole: 'CSR' | 'CUSTOMER' | 'SYSTEM' | 'AGENCY';
  readonly postedAt: string;
}

// ---------------------------------------------------------------------------
// Sub-project B slice 10 — admin types
// ---------------------------------------------------------------------------

export interface ServiceDeskDashboard {
  readonly activeForms: number;
  readonly totalSubmissions: number;
  readonly pendingReview: number;
  readonly partnerAgencies: number;
}

export interface ApplicationItem {
  readonly id: string;
  readonly caseNumber: string;
  readonly formKey: string;
  readonly agency: string;
  readonly title: string;
  readonly status: string;
  readonly priority: string;
  readonly customerId: string | null;
  readonly assigneeId: string | null;
  readonly openedAt: string;
  readonly updatedAt: string;
}

export interface FormDefinition {
  readonly id: string;
  readonly name: string;
  readonly fieldsSchema: Record<string, unknown>;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ExportJob {
  readonly id: string;
  readonly requestedBy: string;
  readonly formKey: string | null;
  readonly fromDate: string;
  readonly toDate: string;
  readonly format: string;
  readonly status: 'PENDING' | 'READY' | 'FAILED';
  readonly filePath: string | null;
  readonly createdAt: string;
  readonly completedAt: string | null;
}

export interface HelpArticle {
  readonly id: string;
  readonly title: string;
  readonly slug: string;
  readonly body: string;
  readonly category: string;
  readonly published: boolean;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

// ---------------------------------------------------------------------------
// Base URL
// ---------------------------------------------------------------------------

const baseUrl = (): string => env.serviceServiceDeskBaseUrl;

// ---------------------------------------------------------------------------
// Case catalog + open-case endpoints (operator file-on-behalf surface).
// ---------------------------------------------------------------------------

/** Wire shape returned by ServiceDeskCatalogResponseDto in the list. */
export interface CatalogForm {
  readonly id: string;
  readonly formKey: string;
  readonly formName: string;
  readonly agency: string;
  readonly displayName: string;
  readonly category: string;
  readonly slaHours: number;
  readonly priorityDefault: string;
  readonly requiresPayment: boolean;
  readonly feeAmount: string | null;
  readonly active: boolean;
}

/** Detail variant — includes the JSON-Schema `fieldsSchema`. */
export interface CatalogFormDetail extends CatalogForm {
  readonly description: string;
  readonly fieldsSchema: Record<string, unknown>;
  readonly escalationSchedule?: readonly { tierLevel: number; afterHours: number }[];
}

/** POST /v1/service-desk/cases body (mirrors OpenCaseSchema). */
export interface OpenCaseRequest {
  readonly formKey: string;
  readonly customerId?: string;
  readonly title: string;
  readonly formData: Record<string, unknown>;
  readonly subjectRef?: string;
  readonly priority?: CasePriority;
  readonly paymentId?: string;
}

/** POST /v1/service-desk/cases — open a case (operator-on-behalf or anonymous). */
export async function openCase(body: OpenCaseRequest): Promise<ServiceDeskCase> {
  return request<ServiceDeskCase>(baseUrl(), '/v1/service-desk/cases', {
    method: 'POST',
    body,
  });
}

/** GET /v1/service-desk/catalog — list active catalog forms. */
export async function listCaseCatalog(): Promise<readonly CatalogForm[]> {
  const res = await request<{
    readonly items: readonly CatalogForm[];
    // eslint-disable-next-line upmp/no-number-money -- row count, not money
    readonly total: number;
    // eslint-disable-next-line upmp/no-number-money -- pagination, not money
    readonly page: number;
    // eslint-disable-next-line upmp/no-number-money -- pagination, not money
    readonly pageSize: number;
  }>(baseUrl(), '/v1/service-desk/catalog', {
    method: 'GET',
    query: { activeOnly: true, pageSize: 100 },
  });
  return res.items;
}

/** GET /v1/service-desk/catalog/:formKey — single form + JSON Schema. */
export async function getCaseCatalogForm(formKey: string): Promise<CatalogFormDetail> {
  return request<CatalogFormDetail>(
    baseUrl(),
    `/v1/service-desk/catalog/${encodeURIComponent(formKey)}`,
    { method: 'GET' },
  );
}

// ---------------------------------------------------------------------------
// Sub-project C slice 4 — case workflow endpoints
// ---------------------------------------------------------------------------

/** GET /v1/service-desk/cases — list with filters. */
export async function listServiceCases(query: {
  readonly status?: CaseStatus;
  readonly priority?: CasePriority;
  readonly category?: string;
  readonly agency?: string;
  readonly assigneeId?: string;
  readonly customerId?: string;
  readonly formKey?: string;
  readonly page?: number;
  readonly pageSize?: number;
} = {}): Promise<{
  readonly items: readonly ServiceDeskCase[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}> {
  return request(baseUrl(), '/v1/service-desk/cases', {
    method: 'GET',
    query: {
      status: query.status,
      priority: query.priority,
      category: query.category,
      agency: query.agency,
      assigneeId: query.assigneeId,
      customerId: query.customerId,
      formKey: query.formKey,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

/** GET /v1/service-desk/cases/:id — fetch single case. */
export async function getServiceCase(id: string): Promise<ServiceDeskCase> {
  return request<ServiceDeskCase>(baseUrl(), `/v1/service-desk/cases/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/** POST /v1/service-desk/cases/:id/transition — generic state-machine transition. */
export async function transitionCase(
  id: string,
  event: CaseTransitionEvent,
  note?: string,
): Promise<ServiceDeskCase> {
  return request<ServiceDeskCase>(
    baseUrl(),
    `/v1/service-desk/cases/${encodeURIComponent(id)}/transition`,
    {
      method: 'POST',
      body: {
        event,
        ...(note !== undefined ? { note } : {}),
      },
    },
  );
}

/** POST /v1/service-desk/cases/:id/comments — add PUBLIC or INTERNAL note. */
export async function addCaseComment(
  id: string,
  body: string,
  visibility: 'PUBLIC' | 'INTERNAL' = 'INTERNAL',
): Promise<ServiceDeskComment> {
  return request<ServiceDeskComment>(
    baseUrl(),
    `/v1/service-desk/cases/${encodeURIComponent(id)}/comments`,
    {
      method: 'POST',
      body: { body, visibility },
    },
  );
}

/** GET /v1/service-desk/cases/:id/comments — list comments including INTERNAL. */
export async function listCaseComments(id: string): Promise<{ readonly items: readonly ServiceDeskComment[] }> {
  return request<{ items: ServiceDeskComment[] }>(
    baseUrl(),
    `/v1/service-desk/cases/${encodeURIComponent(id)}/comments`,
    { method: 'GET' },
  );
}

/** POST /v1/service-desk/cases/:id/escalate — bump escalation tier. */
export async function escalateCase(
  id: string,
  reason: string,
  reassignTo?: string,
): Promise<ServiceDeskCase> {
  return request<ServiceDeskCase>(
    baseUrl(),
    `/v1/service-desk/cases/${encodeURIComponent(id)}/escalate`,
    {
      method: 'POST',
      body: {
        reason,
        ...(reassignTo !== undefined ? { reassignTo } : {}),
      },
    },
  );
}

/** POST /v1/service-desk/cases/:id/resolve — mark RESOLVED. */
export async function resolveCase(
  id: string,
  resolutionType: string,
  resolutionNote: string,
): Promise<ServiceDeskCase> {
  return request<ServiceDeskCase>(
    baseUrl(),
    `/v1/service-desk/cases/${encodeURIComponent(id)}/resolve`,
    {
      method: 'POST',
      body: { resolutionType, resolutionNote },
    },
  );
}

// ---------------------------------------------------------------------------
// Sub-project B slice 10 — admin: Dashboard
// ---------------------------------------------------------------------------

export async function getServiceDeskDashboard(): Promise<ServiceDeskDashboard> {
  return request<ServiceDeskDashboard>(baseUrl(), '/v1/service-desk/dashboard', {
    method: 'GET',
  });
}

// ---------------------------------------------------------------------------
// Admin: Applications
// ---------------------------------------------------------------------------

export async function listApplications(query: {
  readonly page?: number;
  readonly pageSize?: number;
  readonly status?: string;
  readonly agency?: string;
  readonly formKey?: string;
  readonly search?: string;
} = {}): Promise<{
  readonly items: readonly ApplicationItem[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}> {
  return request(baseUrl(), '/v1/service-desk/applications', {
    method: 'GET',
    query: {
      page: query.page,
      pageSize: query.pageSize,
      status: query.status,
      agency: query.agency,
      formKey: query.formKey,
      search: query.search,
    },
  });
}

// ---------------------------------------------------------------------------
// Admin: Form Definitions
// ---------------------------------------------------------------------------

export async function listFormDefinitions(): Promise<{
  readonly items: readonly FormDefinition[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}> {
  return request(baseUrl(), '/v1/service-desk/forms', { method: 'GET' });
}

export async function createFormDefinition(body: {
  name: string;
  fieldsSchema: Record<string, unknown>;
}): Promise<FormDefinition> {
  return request<FormDefinition>(baseUrl(), '/v1/service-desk/forms', {
    method: 'POST',
    body,
  });
}

export async function updateFormDefinition(
  id: string,
  body: { name?: string; fieldsSchema?: Record<string, unknown> },
): Promise<FormDefinition> {
  return request<FormDefinition>(baseUrl(), `/v1/service-desk/forms/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body,
  });
}

export async function deleteFormDefinition(id: string): Promise<void> {
  await request<unknown>(baseUrl(), `/v1/service-desk/forms/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Admin: Export Jobs
// ---------------------------------------------------------------------------

export async function createExportJob(body: {
  formKey?: string;
  fromDate: string;
  toDate: string;
  format?: 'CSV' | 'JSON';
}): Promise<ExportJob> {
  return request<ExportJob>(baseUrl(), '/v1/service-desk/exports', {
    method: 'POST',
    body,
  });
}

export async function getExportJob(id: string): Promise<ExportJob> {
  return request<ExportJob>(baseUrl(), `/v1/service-desk/exports/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

export function getExportDownloadUrl(id: string): string {
  return `${baseUrl()}/v1/service-desk/exports/${encodeURIComponent(id)}/download`;
}

// ---------------------------------------------------------------------------
// Admin: Help Articles
// ---------------------------------------------------------------------------

export async function listHelpArticles(): Promise<{
  readonly items: readonly HelpArticle[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}> {
  return request(baseUrl(), '/v1/service-desk/help-articles', { method: 'GET' });
}

export async function createHelpArticle(body: {
  title: string;
  slug: string;
  body: string;
  category?: string;
  published?: boolean;
}): Promise<HelpArticle> {
  return request<HelpArticle>(baseUrl(), '/v1/service-desk/help-articles', {
    method: 'POST',
    body,
  });
}

export async function updateHelpArticle(
  id: string,
  body: { title?: string; body?: string; category?: string; published?: boolean },
): Promise<HelpArticle> {
  return request<HelpArticle>(
    baseUrl(),
    `/v1/service-desk/help-articles/${encodeURIComponent(id)}`,
    { method: 'PUT', body },
  );
}

export async function deleteHelpArticle(id: string): Promise<void> {
  await request<unknown>(baseUrl(), `/v1/service-desk/help-articles/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Customer-service aggregates (E2E customer-portal):
//   GET    /v1/service-desk/stats              — readStats
//   POST   /v1/service-desk/cases/:id/feedback — submitFeedback
//   GET    /v1/service-desk/feedback           — readFeedback
//   GET    /v1/service-desk/feedback/aggregate — readFeedback
//   POST   /v1/service-desk/feedback/:id/resolve — resolveFeedback
//   GET    /v1/service-desk/segments           — readSegments
// ---------------------------------------------------------------------------

export interface ServiceDeskStats {
  readonly openCases: number;
  readonly breachedSla: number;
  readonly avgResolutionHours: number;
  readonly csat: number;
  readonly feedbackCount: number;
}

export interface FeedbackItem {
  readonly id: string;
  readonly caseId: string;
  readonly caseNumber: string;
  readonly rating: number;
  readonly comment: string | null;
  readonly submittedBy: string;
  readonly submitterRole: 'CUSTOMER' | 'OPERATOR';
  readonly submittedAt: string;
  readonly resolved: boolean;
  readonly resolvedAt: string | null;
  readonly resolvedBy: string | null;
}

export interface FeedbackAggregate {
  readonly csat: number;
  readonly nps: number;
  readonly averageRating: number;
  readonly promoters: number;
  readonly passives: number;
  readonly detractors: number;
  readonly openInbox: number;
  readonly resolvedThisWeek: number;
  readonly totalCount: number;
}

export type SegmentBucket = 'GOLD' | 'SILVER' | 'BRONZE' | 'INACTIVE';

export interface SegmentBucketSummary {
  readonly segment: SegmentBucket;
  readonly customerCount: number;
  readonly description: string;
}

export interface CustomerSegmentMember {
  readonly customerId: string;
  readonly segment: SegmentBucket;
  readonly caseCount: number;
  readonly lastCaseAt: string;
}

export interface SegmentsResponse {
  readonly buckets: readonly SegmentBucketSummary[];
  readonly members: readonly CustomerSegmentMember[];
  readonly totalCustomers: number;
  readonly windowDays: number;
}

export async function getServiceDeskStats(): Promise<ServiceDeskStats> {
  return request<ServiceDeskStats>(baseUrl(), '/v1/service-desk/stats', { method: 'GET' });
}

export async function submitCaseFeedback(
  caseId: string,
  body: { rating: number; comment?: string },
): Promise<{ id: string }> {
  return request<{ id: string }>(
    baseUrl(),
    `/v1/service-desk/cases/${encodeURIComponent(caseId)}/feedback`,
    { method: 'POST', body },
  );
}

export async function listFeedback(query: {
  readonly page?: number;
  readonly pageSize?: number;
  readonly resolved?: boolean;
  readonly rating?: number;
} = {}): Promise<{
  readonly items: readonly FeedbackItem[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}> {
  return request(baseUrl(), '/v1/service-desk/feedback', {
    method: 'GET',
    query: {
      page: query.page,
      pageSize: query.pageSize,
      resolved: query.resolved,
      rating: query.rating,
    },
  });
}

export async function getFeedbackAggregate(): Promise<FeedbackAggregate> {
  return request<FeedbackAggregate>(baseUrl(), '/v1/service-desk/feedback/aggregate', {
    method: 'GET',
  });
}

export async function resolveFeedback(id: string): Promise<void> {
  await request<unknown>(
    baseUrl(),
    `/v1/service-desk/feedback/${encodeURIComponent(id)}/resolve`,
    { method: 'POST' },
  );
}

export async function listCustomerSegments(query: {
  readonly windowDays?: number;
  readonly limit?: number;
} = {}): Promise<SegmentsResponse> {
  return request<SegmentsResponse>(baseUrl(), '/v1/service-desk/segments', {
    method: 'GET',
    query: { windowDays: query.windowDays, limit: query.limit },
  });
}
